import {
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { createPath, UNSAFE_NavigationContext } from "react-router";

import {
  adminDepartureCoordinator,
  getAdminDepartureHistory,
  isApprovedAdminExit,
} from "./adminDepartureRuntime";
import { isAdminSignOutLocked } from "./adminSignOutLock";

export function AdminNavigationGuard({ children }: { children: ReactNode }) {
  const navigation = useContext(UNSAFE_NavigationContext);
  const history = getAdminDepartureHistory();
  const navigator = useMemo(() => {
    const original = navigation.navigator;
    const push: typeof original.push = (...args) => {
      if (isAdminSignOutLocked()) return;
      const perform = () => {
        original.push(...args);
        history.sync();
      };
      if (isApprovedAdminExit()) return perform();
      const [to, state] = args;
      adminDepartureCoordinator.request(
        {
          kind: "push",
          to: typeof to === "string" ? to : createPath(to),
          state,
        },
        perform,
      );
    };
    const replace: typeof original.replace = (...args) => {
      if (isAdminSignOutLocked()) return;
      const perform = () => {
        original.replace(...args);
        history.sync();
      };
      if (isApprovedAdminExit()) return perform();
      const [to, state] = args;
      adminDepartureCoordinator.request(
        {
          kind: "replace",
          to: typeof to === "string" ? to : createPath(to),
          state,
        },
        perform,
      );
    };
    // The underlying history exposes live getters such as location. Preserve
    // those getters and method receivers instead of spreading a stale snapshot.
    return new Proxy(original, {
      get(target, property) {
        if (property === "push") return push;
        if (property === "replace") return replace;
        const value: unknown = Reflect.get(target, property, target);
        return typeof value === "function" ? value.bind(target) : value;
      },
    });
  }, [history, navigation.navigator]);
  const context = useMemo(
    () => ({ ...navigation, navigator }),
    [navigation, navigator],
  );
  // BrowserRouter has assigned its initial index before this layout effect.
  useLayoutEffect(() => {
    history.sync();
  }, [history]);
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const anchor =
        event.target instanceof Element
          ? event.target.closest("a[href]")
          : null;
      if (
        !(anchor instanceof HTMLAnchorElement) ||
        anchor.hasAttribute("download") ||
        (anchor.target && anchor.target !== "_self")
      )
        return;
      const target = new URL(anchor.href);
      if (
        target.origin !== window.location.origin ||
        target.href === window.location.href
      )
        return;
      const to = target.pathname + target.search + target.hash;
      if (!adminDepartureCoordinator.needsInterception({ kind: "push", to }))
        return;
      // Router Links already prevented their native default before this bubble
      // listener. Plain same-tab anchors use their URL as the full operation.
      event.preventDefault();
      navigator.push(to);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [navigator]);
  return (
    <UNSAFE_NavigationContext.Provider value={context}>
      {children}
    </UNSAFE_NavigationContext.Provider>
  );
}
